<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { HOST, API } from '@/utils/http'
import { DatePicker } from 'v-calendar'
import { useScreens } from 'vue-screen-utils'
import { CalendarPlus, Satellite, AlertCircle } from 'lucide-vue-next'
import { formatTime } from '@/utils/datetime'
import { MapFeature } from '@/components/map'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import PlanetPricing from './PlanetPricing.vue'

const emit = defineEmits(['formSubmitted'])
const props = defineProps({
  feature: {
    type: Object,
    required: true
  },
  orgId: {
    type: String,
    required: true
  },
  name: {
    type: String,
    required: true
  }
})

const store = useStore()
const map = computed(() => store.getters['geo/getMap'])
const settings = computed(() => store.getters['user/getUserSettings'])
const isNameEmpty = computed(() => !props.name || props.name.trim() === '')

// Form state
const isSubmitting = ref(false)
const pricingRef = ref(null)
const hasEstimate = ref(false)

const onEstimated = (estimate) => {
  hasEstimate.value = !!estimate
}

// Minimum capture window duration required by Planet API
const MIN_CAPTURE_DAYS = 14

// Date range for capture window
const today = new Date()
const tomorrow = new Date(today)
tomorrow.setDate(tomorrow.getDate() + 1)
const twoWeeksFromNow = new Date(tomorrow)
twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + MIN_CAPTURE_DAYS)

const { mapCurrent } = useScreens({
  xs: '0px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
})
const columns = mapCurrent({ lg: 2 }, 1)
const expanded = mapCurrent({ lg: false }, true)

const disabledDates = ref([{ end: today }])
const range = ref({
  start: tomorrow,
  end: twoWeeksFromNow
})

const isSameYear = (date1, date2) => date1?.getFullYear() === date2?.getFullYear()
const startDate = computed(() => range.value.start ? formatTime(
  range.value.start,
  settings.value?.tz || 'UTC',
  isSameYear(range.value.start, range.value.end) ? 'D MMMM' : 'D MMMM YYYY') : '')
const endDate = computed(() => range.value.end ? formatTime(range.value.end, settings.value?.tz || 'UTC', 'D MMMM YYYY') : '')

// Calculate capture window duration in days
const captureWindowDays = computed(() => {
  if (!range.value.start || !range.value.end) return 0
  const diffTime = range.value.end.getTime() - range.value.start.getTime()
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
})

const isCaptureWindowValid = computed(() => captureWindowDays.value >= MIN_CAPTURE_DAYS)
const captureWindowError = computed(() => {
  if (!range.value.start || !range.value.end) return null
  if (!isCaptureWindowValid.value) {
    return `Capture window must be at least ${MIN_CAPTURE_DAYS} days. Currently: ${captureWindowDays.value} days.`
  }
  return null
})

// Tasking parameters
const selectedOrderType = ref('IMAGE')

// Satellite elevation angle
const elevationAngle = ref([60, 90])

// Create tasking layer
const postLayer = async (layer) => {
  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/org/${props.orgId}/layers`,
      data: layer
    })

    toast({ title: 'Tasking order created successfully' })
    emit('formSubmitted', response.data)
  } catch (error) {
    console.error(error)
    toast({
      title: 'Failed to create tasking order',
      description: error.response?.data?.detail || error.message,
      variant: 'destructive'
    })
  }
}

const onSubmit = async () => {
  isSubmitting.value = true

  if (isNameEmpty.value) {
    toast({ title: 'Name is required', variant: 'destructive' })
    isSubmitting.value = false
    return
  }

  if (!range.value.start || !range.value.end) {
    toast({ title: 'Capture window is required', variant: 'destructive' })
    isSubmitting.value = false
    return
  }

  if (!isCaptureWindowValid.value) {
    toast({ title: captureWindowError.value, variant: 'destructive' })
    isSubmitting.value = false
    return
  }

  try {
    const layer = {
      org_id: parseInt(props.orgId),
      type: 6, // Tasking type
      name: props.name,
      layer: {
        aoi: props.feature.geometry,
        data: {
          order_type: selectedOrderType.value,
          start_time: range.value.start.toISOString(),
          end_time: range.value.end.toISOString(),
          sat_elevation_angle_min: elevationAngle.value[0],
          sat_elevation_angle_max: elevationAngle.value[1],
          estimate: pricingRef.value?.estimate ? {
            estimated_cost: pricingRef.value.estimate.estimated_cost ?? null,
            units: pricingRef.value.estimate.units ?? 'CREDITS',
            area_km2: pricingRef.value.estimate.area_km2 ?? null,
          } : null,
        }
      }
    }

    await postLayer(layer)
  } catch (error) {
    console.error('Error creating tasking order:', error)
    toast({ title: error.message || 'Failed to create tasking order', variant: 'destructive' })
  } finally {
    isSubmitting.value = false
  }
}

// Watch for changes that affect pricing - reset pricing component and estimate state
watch([range, selectedOrderType, elevationAngle, () => props.feature], () => {
  if (pricingRef.value) {
    pricingRef.value.reset()
  }
  hasEstimate.value = false
}, { deep: true })
</script>

<template>
  <div class="space-y-4">
    <!-- Map Preview -->
    <div class="flex items-center justify-between mb-3">
      <MapFeature
        v-if="feature"
        :map="map"
        :feature="feature"
        :key="feature"
        :options="{
          zoomToFeature: true,
          zoomButton: true
        }"
      />
    </div>

    <form class="space-y-4" @submit.prevent="onSubmit">
      <!-- Capture Window -->
      <div class="space-y-2">
        <Label>Capture Window</Label>
        <Popover>
          <PopoverTrigger as-child>
            <Button variant="outline" class="w-full justify-start">
              <CalendarPlus class="w-4 h-4 mr-2" />
              <span v-if="range.start && range.end">{{ startDate }} - {{ endDate }}</span>
              <span v-else class="text-muted-foreground">Select capture dates</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent class="p-0 w-full">
            <div class="p-1">
              <DatePicker
                class="font-sans"
                ref="datePicker"
                borderless
                trim-weeks
                v-model.range="range"
                :step="1"
                :initial-page-position="0"
                :expanded="expanded"
                :disabled-dates="disabledDates"
              />
            </div>
          </PopoverContent>
        </Popover>
        <Alert v-if="captureWindowError" variant="destructive" class="mt-2">
          <AlertCircle class="h-4 w-4" />
          <AlertDescription>{{ captureWindowError }}</AlertDescription>
        </Alert>
        <p v-else class="text-xs text-muted-foreground">
          Minimum capture window: {{ MIN_CAPTURE_DAYS }} days
        </p>
      </div>

      <!-- Satellite Elevation Angle -->
      <div class="space-y-2">
        <Label>Satellite Elevation Angle</Label>
        <p class="text-sm text-muted-foreground">
          Min {{ elevationAngle[0] }}° - Max {{ elevationAngle[1] }}°
        </p>
        <Slider
          v-model="elevationAngle"
          :min="0"
          :max="90"
          :step="5"
          range
          multiple
        />
        <p class="text-xs text-muted-foreground">
          Higher angles (60-90°) provide better image quality with less distortion.
        </p>
      </div>

      <!-- Pricing Section -->
      <div class="border-t pt-4">
        <PlanetPricing
          ref="pricingRef"
          :org-id="orgId"
          :geometry="feature.geometry"
          :start-time="range.start"
          :end-time="range.end"
          :order-type="selectedOrderType"
          :sat-elevation-angle-min="elevationAngle[0]"
          :disabled="!isCaptureWindowValid"
          @estimated="onEstimated"
        />
      </div>

      <!-- Submit Section (only shown after estimate) -->
      <div v-if="hasEstimate" class="pt-4 border-t space-y-3">
        <p class="text-sm text-muted-foreground">
          Captures typically take 1-2 weeks to complete.
        </p>
        <div class="flex justify-end">
          <Button
            type="submit"
            :disabled="isSubmitting || isNameEmpty || !range.start || !range.end || !isCaptureWindowValid"
          >
            <Spinner v-if="isSubmitting" class="w-4 h-4 mr-2" />
            <Satellite v-else class="w-4 h-4 mr-2" />
            <span>{{ isSubmitting ? 'Creating...' : 'Create' }}</span>
          </Button>
        </div>
      </div>
    </form>
  </div>
</template>
